import React, { useState, useRef, useEffect } from 'react';
import assistenteService from '../services/assistenteService';
import { FiSend, FiUser, FiCpu } from 'react-icons/fi';
import toast from 'react-hot-toast';

const AssistenteFinanceiro = ({ chatId, mensagens, setMensagens }) => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const mensagensEndRef = useRef(null);

  const scrollToBottom = () => {
    mensagensEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [mensagens, isLoading]);

  const enviarMensagem = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const mensagemUsuario = input.trim();
    setInput('');

    setMensagens((prev) => [...prev, { role: 'user', content: mensagemUsuario }]);
    setIsLoading(true);

    try {
      const resposta = await assistenteService.perguntar({ chatId, mensagem: mensagemUsuario });
      setMensagens((prev) => [...prev, { role: 'assistant', content: resposta.resposta }]);
    } catch (error) {
      console.error(error);
      toast.error('Não foi possível comunicar com o assistente.');
      setMensagens((prev) => [...prev, {
        role: 'assistant',
        content: 'Desculpe, ocorreu um erro de conexão. Verifique se o servidor está online.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[70vh] w-full bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden font-sans">

      {/* Header — slate sólido, sem gradiente */}
      <div className="bg-slate-900 px-5 py-4 flex items-center justify-between border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-white/10 rounded-md border border-white/15">
            <FiCpu className="text-white text-base" />
          </div>
          <div>
            <h2 className="text-white font-semibold text-sm tracking-wide">Copiloto Financeiro</h2>
            <p className="text-slate-400 text-[10px] uppercase tracking-wider font-medium flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Sessão Ativa
            </p>
          </div>
        </div>
      </div>

      {/* Área de mensagens */}
      <div className="flex-1 overflow-y-auto p-6 bg-white space-y-5">
        {mensagens.map((msg, index) => (
          <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex gap-3 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>

              {/* Avatar */}
              <div className="flex-shrink-0 mt-1">
                {msg.role === 'user' ? (
                  <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center">
                    <FiUser className="text-white text-sm" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
                    <FiCpu className="text-slate-600 text-sm" />
                  </div>
                )}
              </div>

              {/* Bolha */}
              <div className={`px-4 py-3 rounded-lg text-[14px] leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-slate-900 text-white rounded-tr-sm'
                  : 'bg-slate-100 text-slate-800 border border-slate-200 rounded-tl-sm'
              }`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          </div>
        ))}

        {/* Loading dots */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="flex gap-3 max-w-[80%]">
              <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0 mt-1">
                <FiCpu className="text-slate-600 text-sm" />
              </div>
              <div className="px-4 py-3 bg-slate-100 border border-slate-200 rounded-lg rounded-tl-sm flex items-center gap-1.5 h-[46px]">
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={mensagensEndRef} />
      </div>

      {/* Input area */}
      <div className="p-4 bg-white border-t border-slate-200">
        <form onSubmit={enviarMensagem} className="relative flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Pergunte sobre rentabilidade, juros ou prazos..."
            className="w-full bg-white border border-slate-200 text-slate-800 text-sm rounded-md pl-4 pr-12 py-3 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent resize-none h-[52px] min-h-[52px] max-h-[150px]"
            rows="1"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="absolute right-2.5 bottom-2 p-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:bg-slate-200 disabled:cursor-not-allowed transition-colors"
          >
            <FiSend size={15} />
          </button>
        </form>
      </div>

    </div>
  );
};

export default AssistenteFinanceiro;